/**
 * Activity Trail — the forensic timeline.
 *
 * THE core feature of the Trust module. Every query, tool call, retrieval,
 * block, approval, and response on the AI platform is a row here, sortable
 * and filterable by user, agent, workflow, date range, outcome, classification,
 * severity, event type, and framework control relevance.
 *
 * Row click → Activity Detail Sheet with full event forensics (hash-chain
 * proof, parent/child events, input/output JSON, framework control mapping,
 * replay breadcrumb).
 *
 * Exports: filtered CSV + JSON; per-session timeline export.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ScrollText, Search, Download, Clock, User, Bot, Workflow as WorkflowIcon,
  Shield, AlertTriangle, CheckCircle2, XCircle, FileText, Filter,
  ArrowRight, ChevronDown, Sparkles, Activity, Zap, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  OUTCOME_STYLES, SEVERITY_STYLES, CATEGORY_STYLES, CLASSIFICATION_STYLES,
  type ActivityEvent, type EventCategory, type EventOutcome, type Severity,
  type Classification,
} from '@/lib/mock-trust-activity';
import { useTrustActivity } from '@/lib/use-trust-module';
import { useTrustEntities, shortId } from '@/lib/use-trust-entities';
import { ActivityDetailSheet } from '@/components/trust/activity-detail-sheet';
import { triggerDownload, generateCsv } from '@/lib/trust-downloads';

export const Route = createFileRoute('/trust/activity')({
  component: ActivityTrailPage,
});

type PivotView = 'events' | 'by_user' | 'by_agent' | 'by_workflow';
type DateRange = '1h' | '24h' | '7d' | '30d' | 'all';

// ─── Page ───────────────────────────────────────────────────────────────────

function ActivityTrailPage() {
  const navigate = useNavigate();

  // Real-data: audit_logs adapted to ActivityEvent VM.
  // 2026-05-12 browser audit P1 — destructure isError/error so the page
  // can surface a real failure instead of masquerading as "No events
  // match your filters". Previous behaviour hid HTTP 500s as a clean
  // empty state, which the auditor flagged as worse than the 500 itself.
  const {
    data: activityEvents = [],
    isError: activityError,
    error: activityErrorObj,
    isLoading: activityLoading,
  } = useTrustActivity(500);

  // Real-data entity lookups (replaces hardcoded ACTIVITY_USERS/ACTIVITY_AGENTS/ACTIVITY_WORKFLOWS).
  const entities = useTrustEntities();

  // Filters
  const [pivotView, setPivotView] = useState<PivotView>('events');
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [workflowFilter, setWorkflowFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | EventOutcome>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | EventCategory>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | Severity>('all');
  const [classificationFilter, setClassificationFilter] = useState<'all' | Classification>('all');

  const [selected, setSelected] = useState<ActivityEvent | null>(null);

  // ── Filter pipeline ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const now = Date.now();
    const cutoffMs =
      dateRange === '1h'  ? 3_600_000 :
      dateRange === '24h' ? 86_400_000 :
      dateRange === '7d'  ? 604_800_000 :
      dateRange === '30d' ? 2_592_000_000 :
      Infinity;
    const cutoff = now - cutoffMs;

    return activityEvents.filter(e => {
      if (new Date(e.timestamp).getTime() < cutoff) return false;
      if (userFilter !== 'all' && e.userId !== userFilter) return false;
      if (agentFilter !== 'all' && e.agentId !== agentFilter) return false;
      if (workflowFilter !== 'all' && e.workflowId !== workflowFilter) return false;
      if (outcomeFilter !== 'all' && e.outcome !== outcomeFilter) return false;
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (severityFilter !== 'all' && e.severity !== severityFilter) return false;
      if (classificationFilter !== 'all' && !e.classifications.includes(classificationFilter)) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        e.summary.toLowerCase().includes(q)
        || (e.inputSnippet?.toLowerCase().includes(q) ?? false)
        || (e.outputSnippet?.toLowerCase().includes(q) ?? false)
        || (e.targetLabel?.toLowerCase().includes(q) ?? false)
        || (e.targetId?.toLowerCase().includes(q) ?? false)
        || (e.userId?.toLowerCase().includes(q) ?? false)
        || (e.agentId?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [activityEvents, search, userFilter, agentFilter, workflowFilter, dateRange, outcomeFilter, categoryFilter, severityFilter, classificationFilter]);

  // Sorted (default: newest first by timestamp)
  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'timestamp', 'desc');

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = filtered.length;
    const blocked = filtered.filter(e => e.outcome.startsWith('blocked_')).length;
    const failed = filtered.filter(e => e.outcome === 'failed_error' || e.outcome === 'failed_timeout').length;
    const warnings = filtered.filter(e => e.severity === 'warning' || e.severity === 'error' || e.severity === 'critical').length;
    const uniqueUsers = new Set(filtered.map(e => e.userId).filter(Boolean)).size;
    const uniqueAgents = new Set(filtered.map(e => e.agentId).filter(Boolean)).size;
    return { total, blocked, failed, warnings, uniqueUsers, uniqueAgents };
  }, [filtered]);

  // ── Pivot view aggregates ────────────────────────────────────────────────
  const byUser = useMemo(() => {
    const m = new Map<string, { userId: string; count: number; blocked: number; lastActivity: string }>();
    filtered.forEach(e => {
      if (!e.userId) return;
      const cur = m.get(e.userId) ?? { userId: e.userId, count: 0, blocked: 0, lastActivity: e.timestamp };
      cur.count++;
      if (e.outcome.startsWith('blocked_')) cur.blocked++;
      if (new Date(e.timestamp).getTime() > new Date(cur.lastActivity).getTime()) cur.lastActivity = e.timestamp;
      m.set(e.userId, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [filtered]);

  const byAgent = useMemo(() => {
    const m = new Map<string, { agentId: string; count: number; blocked: number; lastActivity: string }>();
    filtered.forEach(e => {
      if (!e.agentId) return;
      const cur = m.get(e.agentId) ?? { agentId: e.agentId, count: 0, blocked: 0, lastActivity: e.timestamp };
      cur.count++;
      if (e.outcome.startsWith('blocked_')) cur.blocked++;
      if (new Date(e.timestamp).getTime() > new Date(cur.lastActivity).getTime()) cur.lastActivity = e.timestamp;
      m.set(e.agentId, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [filtered]);

  const byWorkflow = useMemo(() => {
    const m = new Map<string, { workflowId: string; count: number; failed: number; lastActivity: string }>();
    filtered.forEach(e => {
      if (!e.workflowId) return;
      const cur = m.get(e.workflowId) ?? { workflowId: e.workflowId, count: 0, failed: 0, lastActivity: e.timestamp };
      cur.count++;
      if (e.outcome === 'failed_error' || e.outcome === 'failed_timeout') cur.failed++;
      if (new Date(e.timestamp).getTime() > new Date(cur.lastActivity).getTime()) cur.lastActivity = e.timestamp;
      m.set(e.workflowId, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [filtered]);

  // ── Export ───────────────────────────────────────────────────────────────
  const handleExportCsv = () => {
    const csv = generateCsv(
      ['Seq', 'Timestamp', 'Category', 'Type', 'Outcome', 'Severity', 'Actor', 'Target', 'Summary', 'Classifications', 'Framework Controls', 'Duration (ms)', 'Hash'],
      sorted.map(e => [
        e.sequenceNumber,
        e.timestamp,
        e.category,
        e.type,
        e.outcome,
        e.severity,
        [e.userId, e.agentId, e.workflowId].filter(Boolean).join(' | '),
        [e.targetLabel ?? e.targetId].filter(Boolean).join(' | '),
        e.summary,
        e.classifications.join(' | '),
        e.frameworkControls.join(' | '),
        e.durationMs ?? '',
        e.hash,
      ]),
    );
    triggerDownload({
      filename: `activity-trail-${new Date().toISOString().split('T')[0]}.csv`,
      content: csv,
      mimeType: 'text/csv',
    });
    toast.success(`Exported ${sorted.length} events`);
  };

  const handleExportJson = () => {
    triggerDownload({
      filename: `activity-trail-${new Date().toISOString().split('T')[0]}.json`,
      content: JSON.stringify(sorted, null, 2),
      mimeType: 'application/json',
    });
    toast.success(`Exported ${sorted.length} events as JSON`);
  };

  const handleResetFilters = () => {
    setSearch('');
    setUserFilter('all');
    setAgentFilter('all');
    setWorkflowFilter('all');
    setDateRange('7d');
    setOutcomeFilter('all');
    setCategoryFilter('all');
    setSeverityFilter('all');
    setClassificationFilter('all');
  };

  const filtersActive =
    search !== '' || userFilter !== 'all' || agentFilter !== 'all' || workflowFilter !== 'all'
    || dateRange !== '7d' || outcomeFilter !== 'all' || categoryFilter !== 'all'
    || severityFilter !== 'all' || classificationFilter !== 'all';

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">
      {/* Header + Export */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-primary" />
            Activity Trail
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Forensic timeline of every action on the AI platform. Every query, tool call, retrieval,
            block, approval, and response is an auditable event on the hash-chain.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5">
              <Download className="h-3.5 w-3.5" />
              Export ({sorted.length})
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Filtered results</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleExportCsv}>
              <FileText className="h-3.5 w-3.5 mr-2" /> Export CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportJson}>
              <FileText className="h-3.5 w-3.5 mr-2" /> Export JSON
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Events</span>
              <Activity className="h-3.5 w-3.5 text-primary" />
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-[10px] text-muted-foreground">in selected window</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Blocked</span>
              <Lock className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            </div>
            <p className={cn('text-2xl font-bold', stats.blocked > 0 && 'text-red-600 dark:text-red-400')}>
              {stats.blocked}
            </p>
            <p className="text-[10px] text-muted-foreground">by policy/gate/injection</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Failures</span>
              <XCircle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            </div>
            <p className={cn('text-2xl font-bold', stats.failed > 0 && 'text-orange-600 dark:text-orange-400')}>
              {stats.failed}
            </p>
            <p className="text-[10px] text-muted-foreground">errors + timeouts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Warnings+</span>
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className={cn('text-2xl font-bold', stats.warnings > 0 && 'text-amber-600 dark:text-amber-400')}>
              {stats.warnings}
            </p>
            <p className="text-[10px] text-muted-foreground">warning/error/critical</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Unique Users</span>
              <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-2xl font-bold">{stats.uniqueUsers}</p>
            <p className="text-[10px] text-muted-foreground">of {entities.users.length} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Active Agents</span>
              <Bot className="h-3.5 w-3.5 text-pink-600 dark:text-pink-400" />
            </div>
            <p className="text-2xl font-bold">{stats.uniqueAgents}</p>
            <p className="text-[10px] text-muted-foreground">of {entities.agents.length} deployed</p>
          </CardContent>
        </Card>
      </div>

      {/* Pivot tabs */}
      <div className="flex items-center gap-1 border-b">
        {([
          { id: 'events' as const,       label: 'All Events',     icon: Activity },
          { id: 'by_user' as const,      label: 'By User',        icon: User },
          { id: 'by_agent' as const,     label: 'By Agent',       icon: Bot },
          { id: 'by_workflow' as const,  label: 'By Workflow',    icon: WorkflowIcon },
        ]).map(pv => {
          const Icon = pv.icon;
          const active = pivotView === pv.id;
          return (
            <button
              key={pv.id}
              onClick={() => setPivotView(pv.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 transition-colors',
                active
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {pv.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search events, inputs, targets..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>

        <Select value={dateRange} onValueChange={v => setDateRange(v as DateRange)}>
          <SelectTrigger className="w-28 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">Last hour</SelectItem>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>

        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="User" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {entities.users.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No users found</div>
            )}
            {entities.users.map(u => (
              <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Agent" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agents</SelectItem>
            {entities.agents.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No agents found</div>
            )}
            {entities.agents.map(a => (
              <SelectItem key={a.id} value={a.id} className="text-xs">{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={workflowFilter} onValueChange={setWorkflowFilter}>
          <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="Workflow" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workflows</SelectItem>
            {entities.workflows.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No workflows found</div>
            )}
            {entities.workflows.map(w => (
              <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as typeof categoryFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(Object.keys(CATEGORY_STYLES) as EventCategory[]).map(c => (
              <SelectItem key={c} value={c} className="text-xs">{CATEGORY_STYLES[c].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={outcomeFilter} onValueChange={v => setOutcomeFilter(v as typeof outcomeFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Outcome" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            {(Object.keys(OUTCOME_STYLES) as EventOutcome[]).map(o => (
              <SelectItem key={o} value={o} className="text-xs">{OUTCOME_STYLES[o].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={v => setSeverityFilter(v as typeof severityFilter)}>
          <SelectTrigger className="w-28 h-9 text-xs"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {(Object.keys(SEVERITY_STYLES) as Severity[]).map(s => (
              <SelectItem key={s} value={s} className="text-xs">{SEVERITY_STYLES[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={classificationFilter} onValueChange={v => setClassificationFilter(v as typeof classificationFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Classification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classifications</SelectItem>
            {(Object.keys(CLASSIFICATION_STYLES) as Classification[]).map(c => (
              <SelectItem key={c} value={c} className="text-xs">{CLASSIFICATION_STYLES[c].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtersActive && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={handleResetFilters}>
            <Filter className="h-3 w-3 mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* 2026-05-12 browser audit P1 — surface real backend failure
          instead of masking it as "No events match your filters". */}
      {activityError && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm"
        >
          <div className="font-medium text-destructive">
            Activity feed failed to load
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {activityErrorObj instanceof Error
              ? activityErrorObj.message
              : 'The /trust-module/activity endpoint returned a non-success status. Check the API logs and retry.'}
          </div>
        </div>
      )}

      {/* View body */}
      {pivotView === 'events' && (
        <EventsTable
          sorted={sorted}
          sortConfig={sortConfig}
          handleSort={handleSort}
          onSelect={setSelected}
          entities={entities}
          isError={activityError}
          isLoading={activityLoading}
        />
      )}

      {pivotView === 'by_user' && (
        <PivotPanel
          kind="user"
          rows={byUser.map(r => {
            const u = entities.lookup.user(r.userId);
            return {
              id: r.userId,
              name: u?.name ?? `user:${shortId(r.userId)}`,
              subtitle: u?.role ?? '',
              count: r.count,
              blockedOrFailed: r.blocked,
              blockedLabel: 'blocked',
              lastActivity: r.lastActivity,
              linkedId: null,
            };
          })}
          onPickRow={(id) => setUserFilter(id)}
          onReset={() => setPivotView('events')}
        />
      )}

      {pivotView === 'by_agent' && (
        <PivotPanel
          kind="agent"
          rows={byAgent.map(r => {
            const agent = entities.lookup.agent(r.agentId);
            return {
              id: r.agentId,
              name: agent?.name ?? `agent:${shortId(r.agentId)}`,
              subtitle: agent?.type ?? '',
              count: r.count,
              blockedOrFailed: r.blocked,
              blockedLabel: 'blocked',
              lastActivity: r.lastActivity,
              linkedId: agent?.linkedAgentId ?? null,
            };
          })}
          onPickRow={(id) => setAgentFilter(id)}
          onReset={() => setPivotView('events')}
          onGoToLinked={(linkedId) => navigate({ to: '/agents/$agentId', params: { agentId: linkedId } as any })}
        />
      )}

      {pivotView === 'by_workflow' && (
        <PivotPanel
          kind="workflow"
          rows={byWorkflow.map(r => {
            const wf = entities.lookup.workflow(r.workflowId);
            return {
              id: r.workflowId,
              name: wf?.name ?? `workflow:${shortId(r.workflowId)}`,
              subtitle: '',
              count: r.count,
              blockedOrFailed: r.failed,
              blockedLabel: 'failed',
              lastActivity: r.lastActivity,
              linkedId: wf?.linkedWorkflowId ?? null,
            };
          })}
          onPickRow={(id) => setWorkflowFilter(id)}
          onReset={() => setPivotView('events')}
          onGoToLinked={(linkedId) => navigate({ to: '/automate/workflows/$id', params: { id: linkedId } as any })}
        />
      )}

      {/* Detail Sheet */}
      <ActivityDetailSheet
        event={selected}
        onClose={() => setSelected(null)}
        allEvents={activityEvents}
        onEventChange={(next) => setSelected(next)}
      />
    </div>
  );
}

// ─── Events Table ───────────────────────────────────────────────────────────

function EventsTable({
  sorted, sortConfig, handleSort, onSelect, entities, isError, isLoading,
}: {
  sorted: ActivityEvent[];
  sortConfig: any;
  handleSort: (key: string) => void;
  onSelect: (e: ActivityEvent) => void;
  entities: ReturnType<typeof useTrustEntities>;
  isError?: boolean;
  isLoading?: boolean;
}) {
  // 2026-05-12 browser audit P1 — when the backend request failed,
  // suppress the empty-state copy. The parent already renders an
  // explicit error banner; showing "No events" beside it would still
  // imply a clean tenant. Render nothing here so the alert above is
  // the only thing the user sees.
  if (isError) return null;
  if (isLoading && sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Activity className="h-10 w-10 text-muted-foreground/20 mb-3 animate-pulse" />
        <p className="text-sm font-medium">Loading activity…</p>
      </div>
    );
  }
  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Activity className="h-10 w-10 text-muted-foreground/20 mb-3 dark:text-muted-foreground/40" />
        <p className="text-sm font-medium">No events match your filters</p>
        <p className="text-xs text-muted-foreground mt-1">Try widening the date range or clearing filters</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-x-auto">
      <div className="min-w-[1400px]">
        <div className="grid grid-cols-[90px_150px_110px_minmax(340px,2fr)_180px_140px_100px_110px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
          <SortableHeader label="Seq" sortKey="sequenceNumber" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Time" sortKey="timestamp" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Category" sortKey="category" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Event" sortKey="summary" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Actor" sortKey="userId" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Target" sortKey="targetLabel" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Outcome" sortKey="outcome" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Severity" sortKey="severity" currentSort={sortConfig} onSort={handleSort} />
        </div>
        <div className="divide-y">
          {sorted.slice(0, 300).map(e => (
            <EventRow key={e.id} ev={e} onSelect={onSelect} entities={entities} />
          ))}
          {sorted.length > 300 && (
            <div className="p-3 text-center text-xs text-muted-foreground bg-muted/20">
              Showing first 300 of {sorted.length.toLocaleString()} events. Refine filters to narrow down, or export for full results.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Event Row ──────────────────────────────────────────────────────────────

// Neutral fallback so an audit event whose category/outcome/severity/classification
// the server emits but this fixed style map doesn't know (the adapters cast
// `s.category as EventCategory` with no validation) degrades to a plain badge
// instead of crashing the ENTIRE /trust/activity page via `.color` on undefined.
const FALLBACK_STYLE = { label: '', color: 'text-muted-foreground border-muted-foreground/30' } as const;

function EventRow({ ev, onSelect, entities }: { ev: ActivityEvent; onSelect: (e: ActivityEvent) => void; entities: ReturnType<typeof useTrustEntities> }) {
  const cat = CATEGORY_STYLES[ev.category] ?? { ...FALLBACK_STYLE, label: ev.category };
  const oc = OUTCOME_STYLES[ev.outcome] ?? { ...FALLBACK_STYLE, label: ev.outcome };
  const sev = SEVERITY_STYLES[ev.severity] ?? { ...FALLBACK_STYLE, label: ev.severity };
  const isBlocked = (ev.outcome ?? '').startsWith('blocked_');

  const actor = useMemo(() => {
    if (ev.actorType === 'user' && ev.userId) {
      const u = entities.lookup.user(ev.userId);
      return u
        ? { icon: User, label: u.name, sub: u.role, color: 'blue' }
        : { icon: User, label: `user:${shortId(ev.userId)}`, sub: 'unknown', color: 'zinc' };
    }
    if (ev.actorType === 'agent' && ev.agentId) {
      const a = entities.lookup.agent(ev.agentId);
      return a
        ? { icon: Bot, label: a.name, sub: a.type, color: a.iconColor }
        : { icon: Bot, label: `agent:${shortId(ev.agentId)}`, sub: 'unknown', color: 'zinc' };
    }
    if (ev.actorType === 'workflow' && ev.workflowId) {
      const w = entities.lookup.workflow(ev.workflowId);
      return w
        ? { icon: WorkflowIcon, label: w.name, sub: 'workflow', color: w.iconColor }
        : { icon: WorkflowIcon, label: `workflow:${shortId(ev.workflowId)}`, sub: 'unknown', color: 'zinc' };
    }
    return { icon: Zap, label: 'System', sub: 'automated', color: 'zinc' };
  }, [ev, entities]);

  return (
    <div
      className={cn(
        'grid grid-cols-[90px_150px_110px_minmax(340px,2fr)_180px_140px_100px_110px] gap-3 px-4 py-3 items-center text-xs hover:bg-muted/30 transition-colors cursor-pointer group',
        isBlocked && 'bg-red-500/[0.03]',
      )}
      onClick={() => onSelect(ev)}
    >
      <span className="font-mono text-muted-foreground">#{ev.sequenceNumber}</span>
      <span className="text-muted-foreground" title={ev.timestamp}>{relativeTime(ev.timestamp)}</span>
      <Badge variant="outline" className={cn('text-[9px] w-fit', cat.color)}>{cat.label}</Badge>
      <div className="min-w-0">
        <p className="font-medium truncate">{ev.summary}</p>
        <div className="flex items-center gap-1 mt-0.5">
          {ev.classifications.map(c => {
            const cs = CLASSIFICATION_STYLES[c] ?? { ...FALLBACK_STYLE, label: c };
            return (
              <Badge key={c} variant="outline" className={cn('text-[8px] h-3.5 px-1', cs.color)}>
                {cs.label}
              </Badge>
            );
          })}
          {ev.frameworkControls.slice(0, 2).map(fc => (
            <span key={fc} className="text-[9px] text-muted-foreground font-mono">{fc}</span>
          ))}
          {ev.frameworkControls.length > 2 && (
            <span className="text-[9px] text-muted-foreground">+{ev.frameworkControls.length - 2}</span>
          )}
        </div>
      </div>
      <div className="min-w-0">
        {actor && (
          <div className="flex items-center gap-1.5">
            <actor.icon className={cn('h-3.5 w-3.5', `text-${actor.color}-600 dark:text-${actor.color}-400`)} />
            <div className="min-w-0">
              <p className="truncate font-medium">{actor.label}</p>
              <p className="truncate text-[9px] text-muted-foreground">{actor.sub}</p>
            </div>
          </div>
        )}
      </div>
      <div className="min-w-0">
        {ev.targetLabel ? (
          <div>
            <p className="truncate font-mono text-[10px]">{ev.targetLabel}</p>
            <p className="truncate text-[9px] text-muted-foreground capitalize">{ev.targetType}</p>
          </div>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </div>
      <Badge variant="outline" className={cn('text-[9px] w-fit', oc.color)}>{oc.label}</Badge>
      <Badge variant="outline" className={cn('text-[9px] w-fit', sev.color)}>{sev.label}</Badge>
    </div>
  );
}

// ─── Pivot Panel (By User / Agent / Workflow) ───────────────────────────────

function PivotPanel({
  kind, rows, onPickRow, onReset, onGoToLinked,
}: {
  kind: 'user' | 'agent' | 'workflow';
  rows: Array<{ id: string; name: string; subtitle: string; count: number; blockedOrFailed: number; blockedLabel: string; lastActivity: string; linkedId: string | null }>;
  onPickRow: (id: string) => void;
  onReset: () => void;
  onGoToLinked?: (linkedId: string) => void;
}) {
  return (
    <div>
      <div className="rounded-lg border overflow-hidden">
        <div className="grid grid-cols-[minmax(280px,2fr)_120px_120px_140px_140px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          <span>{kind === 'user' ? 'User' : kind === 'agent' ? 'Agent' : 'Workflow'}</span>
          <span>Events</span>
          <span className="capitalize">{rows[0]?.blockedLabel ?? 'Blocked'}</span>
          <span>Last Activity</span>
          <span>Actions</span>
        </div>
        <div className="divide-y">
          {rows.map(r => (
            <div
              key={r.id}
              className="grid grid-cols-[minmax(280px,2fr)_120px_120px_140px_140px] gap-3 px-4 py-3 items-center text-xs hover:bg-muted/30 cursor-pointer"
              onClick={() => { onPickRow(r.id); onReset(); }}
            >
              <div className="min-w-0">
                <p className="font-medium truncate">{r.name}</p>
                <p className="truncate text-[10px] text-muted-foreground">{r.subtitle}</p>
              </div>
              <span className="font-semibold">{r.count}</span>
              <span className={cn(r.blockedOrFailed > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground')}>
                {r.blockedOrFailed}
              </span>
              <span className="text-muted-foreground">{relativeTime(r.lastActivity)}</span>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => { onPickRow(r.id); onReset(); }}>
                  View events <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
                {onGoToLinked && r.linkedId && (
                  <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => onGoToLinked(r.linkedId!)}>
                    Open <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
